<template>
  <div>
    <div class="d-flex align-center">
      <s-money-input
        v-if="!alwaysMasked && canShowRealValue"
        ref="moneyInput"
        :label="label"
        v-model="plainValue"
        :readonly="readonly"
        :disabled="disabled"
        :style="inputStyle"
        @keyupEnter="$emit('keyupEnter', $event)"
        @keyup.enter="$emit('keyupEnter', $event); $emit('submit', $event)"
        @keydown.enter="$emit('keydownEnter', $event)"
      />
      <s-text
        v-else
        ref="textInput"
        :label="label"
        :value="maskValue"
        readonly
        :disabled="disabled"
        :style="inputStyle"
        @keyupEnter="$emit('keyupEnter', $event)"
        @keyup.enter="$emit('keyupEnter', $event); $emit('submit', $event)"
        @keydown.enter="$emit('keydownEnter', $event)"
      />

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            small
            class="ml-1 mt-1"
            v-bind="attrs"
            v-on="on"
            :disabled="disabled"
            :loading="loadingAccess"
            @click="openUnlockDialog"
          >
            <v-icon small color="warning">mdi-pencil</v-icon>
          </v-btn>
        </template>
        <span>{{ canShowRealValue ? "Editar campo protegido" : "Registrar sueldo protegido" }}</span>
      </v-tooltip>
    </div>

    <v-dialog v-model="dialogUser" max-width="420px" persistent>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold">Login administrador</v-card-title>
        <v-card-text>
          <v-alert v-if="unlockError" type="error" dense outlined class="mb-3">
            {{ unlockError }}
          </v-alert>

          <v-text-field
            v-model="adminUser"
            label="Usuario administrador"
            dense
            outlined
            autocomplete="off"
            class="mb-2"
            @keyup.enter="authenticateAdmin"
          />

          <v-text-field
            v-model="adminPassword"
            :type="showPassword ? 'text' : 'password'"
            label="Contrasena de administrador"
            dense
            outlined
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="showPassword = !showPassword"
            @keyup.enter="authenticateAdmin"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingAccess" @click="dialogUser = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="loadingAccess" @click="authenticateAdmin">Continuar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogAmount" max-width="440px" persistent>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold">Registrar sueldo protegido</v-card-title>
        <v-card-text>
          <v-alert v-if="unlockError" type="error" dense outlined class="mb-3">
            {{ unlockError }}
          </v-alert>

          <v-text-field
            v-model="amountInput"
            label="Monto"
            :type="showAmount ? 'number' : 'password'"
            min="0"
            dense
            outlined
            class="mb-2"
            :append-icon="showAmount ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="showAmount = !showAmount"
            @keyup.enter="saveAmountWithAdmin"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingAccess" @click="backToUserDialog">Atras</v-btn>
          <v-btn color="primary" :disabled="loadingAccess" @click="saveAmountWithAdmin">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ContractSignService from "@/services/HumanResource/ContractSignService";

export default {
  name: "sProtectedAdminField",
  props: {
    value: {
      type: [String, Number, null],
      default: null,
    },
    label: {
      type: String,
      default: "Campo",
    },
    maskValue: {
      type: String,
      default: "*****",
    },
    encrypted: {
      type: Boolean,
      default: false,
    },
    canViewByDefault: {
      type: Boolean,
      default: false,
    },
    alwaysMasked: {
      type: Boolean,
      default: true,
    },
    agtID: {
      type: Number,
      default: 0,
    },
    pstID: {
      type: Number,
      default: 0,
    },
    autoCheckAccess: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    inputStyle: {
      type: String,
      default: "flex:1",
    },
    moduleKey: {
      type: String,
      default: "CONTRCNS_CONTTO",
    },
  },
  data() {
    return {
      plainValue: "",
      loadingAccess: false,
      dialogUser: false,
      dialogAmount: false,
      showPassword: false,
      showAmount: false,
      adminUser: "",
      adminPassword: "",
      amountInput: "",
      unlockError: "",
      hasHierarchyAccess: false,
      hasAdminUnlock: false,
      lastSubmittedAmount: null,
    };
  },
  computed: {
    canPersistValue() {
      return this.canViewByDefault || this.hasHierarchyAccess || this.hasAdminUnlock;
    },
    canShowRealValue() {
      return !this.alwaysMasked && this.canPersistValue;
    },
  },
  created() {
    this.emitAccessState();
  },
  watch: {
    value: {
      handler() {
        this.syncPlainValue();
      },
      immediate: true,
    },
    plainValue(newValue) {
      if (!this.canShowRealValue) {
        return;
      }

      const payload = this.encrypted
        ? this.encryptSafe(newValue)
        : newValue;

      this.$emit("input", payload);
    },
    pstID() {
      if (this.autoCheckAccess) {
        this.checkAccessByHierarchy();
      }
    },
    agtID() {
      if (this.autoCheckAccess) {
        this.checkAccessByHierarchy();
      }
    },
    canShowRealValue() {
      this.syncPlainValue();
      this.emitAccessState();
    },
  },
  mounted() {
    if (this.autoCheckAccess) {
      this.checkAccessByHierarchy();
    }
  },
  methods: {
    decryptSafe(rawValue) {
      if (!rawValue || rawValue === this.maskValue) {
        return "";
      }

      if (!this.encrypted) {
        return rawValue;
      }

      try {
        return this.$fun.decryptSalary ? this.$fun.decryptSalary(rawValue) : rawValue;
      } catch {
        return rawValue;
      }
    },
    encryptSafe(plainValue) {
      if (!this.encrypted) {
        return plainValue;
      }

      try {
        return this.$fun.encryptSalary ? this.$fun.encryptSalary(String(plainValue || "")) : plainValue;
      } catch {
        return plainValue;
      }
    },
    syncPlainValue() {
      if (!this.canShowRealValue) {
        this.plainValue = "";
        return;
      }

      this.plainValue = this.decryptSafe(this.value);
    },
    async checkAccessByHierarchy() {
      if (this.disabled) {
        return;
      }

      if (!this.pstID && !this.agtID) {
        this.hasHierarchyAccess = false;
        this.emitAccessState();
        return;
      }

      this.loadingAccess = true;
      try {
        const payload = {
          AgtID: this.agtID > 0 ? this.agtID : null,
          PstID: this.pstID > 0 ? this.pstID : null,
        };

        const response = await ContractSignService.salaryHierarchyAccess(payload, this.$fun.getUserID());
        const data = response?.data || {};

        this.hasHierarchyAccess = !!data.canAccess;

        if (!this.hasHierarchyAccess) {
          this.hasAdminUnlock = false;
        }
      } catch {
        this.hasHierarchyAccess = false;
      } finally {
        this.loadingAccess = false;
        this.emitAccessState();
      }
    },
    openUnlockDialog() {
      this.unlockError = "";
      this.amountInput = "";
      this.adminUser = this.getLoggedAdminUser() || "";
      this.adminPassword = "";
      this.hasAdminUnlock = false;
      this.showPassword = false;
      this.showAmount = false;
      this.dialogAmount = false;
      this.dialogUser = true;
    },
    backToUserDialog() {
      this.unlockError = "";
      this.dialogAmount = false;
      this.dialogUser = true;
    },
    getLoggedAdminUser() {
      return this.$fun?.getUserInfoLogin ? this.$fun.getUserInfoLogin() : "";
    },
    async authenticateAdmin() {
      const adminUserToValidate = (this.adminUser || "").trim();
      if (!adminUserToValidate) {
        this.unlockError = "Ingrese el usuario administrador.";
        return;
      }

      if (!this.adminPassword) {
        this.unlockError = "Ingrese la contrasena de administrador.";
        return;
      }

      this.loadingAccess = true;
      try {
        const payload = {
          AgtID: this.agtID > 0 ? this.agtID : null,
          PstID: this.pstID > 0 ? this.pstID : null,
          AdminUsrName: adminUserToValidate,
          AdminUsrPassword: this.adminPassword,
          ModuleKey: this.moduleKey,
        };

        const response = await ContractSignService.authorizeSalaryField(payload, this.$fun.getUserID());
        const data = response?.data || {};

        if (!data.authorized) {
          this.unlockError = "No autorizado para desbloquear el campo.";
          return;
        }

        this.hasAdminUnlock = true;
        this.$emit("admin-unlocked", data);
        this.emitAccessState();

        this.unlockError = "";
        this.adminUser = adminUserToValidate;
        this.adminPassword = "";
        this.dialogUser = false;
        this.dialogAmount = true;
      } catch (error) {
        this.unlockError =
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Credenciales invalidas o sin permisos.";
      } finally {
        this.loadingAccess = false;
      }
    },
    saveAmountWithAdmin() {
      this.unlockError = "";

      if (!this.hasAdminUnlock) {
        this.unlockError = "Debe autenticarse como administrador antes de registrar el monto.";
        return;
      }

      if (this.amountInput === "" || this.amountInput === null || this.amountInput === undefined) {
        this.unlockError = "Ingrese el monto a guardar.";
        return;
      }

      const numericAmount = Number(this.amountInput);
      if (Number.isNaN(numericAmount) || numericAmount <= 0) {
        this.unlockError = "Ingrese un monto mayor a 0.";
        return;
      }

      this.lastSubmittedAmount = numericAmount;

      const salaryPayload = this.encrypted
        ? this.encryptSafe(numericAmount)
        : numericAmount;

      this.$emit("input", salaryPayload);

      this.dialogAmount = false;
      this.$emit("value-saved", { amount: numericAmount, encrypted: salaryPayload });
      this.$emit("keyupEnter", salaryPayload);
      this.$emit("keyup.enter", salaryPayload);
      this.$emit("submit", salaryPayload);
      this.emitAccessState();
    },
    emitAccessState() {
      this.$emit("access-changed", {
        canShowRealValue: this.canShowRealValue,
        canPersistValue: this.canPersistValue,
        hasHierarchyAccess: this.hasHierarchyAccess,
        hasAdminUnlock: this.hasAdminUnlock,
        lastSubmittedAmount: this.lastSubmittedAmount,
      });
    },
    focus() {
      const f = this.$refs.moneyInput || this.$refs.textInput;
      if (f && typeof f.focus === "function") {
        f.focus();
      }
    },
    error(msg) {
      const f = this.$refs.moneyInput || this.$refs.textInput;
      if (f && typeof f.error === "function") {
        f.error(msg);
      }
    },
  },
};
</script>
